'use client'

import { JSX, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text } from '@chakra-ui/react'
import { Location, Region } from '@leid/api-locations'
import { Manager } from '@leid/api-managers'
import { GetPriceResponse, ProductAvailabilityInfo } from '@leid/api-orders'
import { FormikErrors, FormikTouched } from 'formik'

import { CustomerInfo } from 'app/utils/authentication'
import { buildDate } from 'app/utils/dates'
import { ItemApi } from 'appTypes/items'
import { SortingInfo } from 'appTypes/mapper'
import { CartFormValues, OrderItemInfo } from 'appTypes/orders'
import { DateTimePicker } from 'components/form/DateTimePicker'
import {
  AmountChangePredicate,
  CheckIfRerentPredicate,
  CheckPeriodPredicate,
  DateTimePickerChangeHandler,
  DateTimePickerDatePredicate,
  DateTimePickerHandle,
  DateTimePickerHourPredicate,
  RegionLocationPickerPredicate,
  VisibleSectionEnum,
} from 'components/form/DateTimePicker/interfaces'
import { isAboveBreakpoint, isBelowBreakpoint } from 'utils/breakpoints'

import AccessoryItems from '../AccessoryItems'
import RelatedProducts from '../RelatedProducts'

import AmountSelector from './components/AmountSelector'
import IncludeDeductiblesSwitch from './components/IncludeDeductiblesSwitch'
import LocationSelector from './components/LocationsSelector'
import RentalSectionFooter from './components/RentalSectionFooter'
import RentalSectionHeader from './components/RentalSectionHeader'
import { SubmitStatus } from './interfaces'
import { AdditionalOptionsWrapper, DatePickersWrapper, RentalSectionWrapper } from './styles'

type Props = {
  orderItemInfo: OrderItemInfo | undefined
  step: number | undefined
  isRerent: boolean
  isProlonging: boolean
  product: ItemApi
  productLocations: Location[]
  productPrice: GetPriceResponse | null
  productCalendarList: ProductAvailabilityInfo
  customerInfo: CustomerInfo
  regions: Region[]
  manager: Manager | undefined
  isEditMode: boolean
  isRentalBeyondMaxTime: boolean
  sortingInfo: SortingInfo | undefined

  handlePickupChange: DateTimePickerChangeHandler
  handleReturnChange: DateTimePickerChangeHandler
  handleRegionLocationChange: RegionLocationPickerPredicate
  handleAmountChange: AmountChangePredicate
  checkIfCorrectPeriod: CheckPeriodPredicate
  checkIfRerent: CheckIfRerentPredicate
  shouldDisablePickupDate: DateTimePickerDatePredicate
  shouldDisablePickupHour: DateTimePickerHourPredicate
  shouldDisableReturnDate: DateTimePickerDatePredicate
  shouldDisableReturnHour: DateTimePickerHourPredicate

  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<CartFormValues>>
  isSubmitting: boolean
  setValues: (
    values: React.SetStateAction<CartFormValues>,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<CartFormValues>>
  values: CartFormValues
  setTouched: (
    touched: FormikTouched<CartFormValues>,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<CartFormValues>>
  touched: FormikTouched<CartFormValues>
  submitForm: () => Promise<void>
  productPriceLoading: boolean
}

const RentalSectionClient = (props: Props): JSX.Element => {
  const {
    setFieldValue,
    isSubmitting,
    setValues,
    values,
    setTouched,
    touched,
    submitForm,
    step: parsedStep,
    isRerent: parsedIsRerent,
    orderItemInfo,
    isProlonging,
    product,
    productLocations,
    productPrice,
    productCalendarList,
    customerInfo,
    regions,
    manager,
    isEditMode,
    isRentalBeyondMaxTime,
    sortingInfo,
    handlePickupChange,
    handleReturnChange,
    handleRegionLocationChange,
    handleAmountChange,
    checkIfCorrectPeriod,
    checkIfRerent,
    shouldDisablePickupDate,
    shouldDisablePickupHour,
    shouldDisableReturnDate,
    shouldDisableReturnHour,
    productPriceLoading,
  } = props

  const { t } = useTranslation(['common'])

  const [step, setStep] = useState(parsedStep ?? 1)
  const [isRerent, setIsRerent] = useState(isProlonging ? orderItemInfo?.orderItem?.isRerent ?? false : parsedIsRerent)
  const [nextIsDisabled, setNextIsDisabled] = useState(true)
  const [regionHasAvailableLocations, setRegionHasAvailableLocations] = useState<boolean | undefined>(undefined)
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle')
  const [defaultPickupCollapsed] = useState(isBelowBreakpoint('lg') && isProlonging)
  const [defaultReturnCollapsed] = useState(isBelowBreakpoint('lg') && !isProlonging)
  const [sortingInfoCache, setSortingInfoCache] = useState(sortingInfo)

  const pickupRef = useRef<DateTimePickerHandle>(null)
  const returnRef = useRef<DateTimePickerHandle>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isEditMode) {
      setFieldValue('includeInsurance', !isRerent)
      setFieldValue('includeDeductibles', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRerent])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  const goToNextStep = useCallback(() => {
    scrollToTop()
    setStep((prev) => prev + 1)
  }, [])

  const goToPrevStep = useCallback(() => {
    scrollToTop()
    setStep((prev) => prev - 1)
  }, [])

  const handleSubmit = useCallback(() => {
    setSubmitStatus('pending')
    submitForm()
      .then(() => {
        setSubmitStatus('success')
      })
      .catch(() => {
        setSubmitStatus('failed')
      })
  }, [submitForm])

  const onPickupChange: DateTimePickerChangeHandler = useCallback(
    (newValues) => {
      handlePickupChange(newValues)

      returnRef.current?.reset()
      returnRef.current?.changeDisplayedMonth(newValues.month, newValues.year)
      returnRef.current?.changeVisibleSection(VisibleSectionEnum.DATE)

      if (isBelowBreakpoint('lg')) {
        pickupRef.current?.collapse()
        returnRef.current?.expand()
        document
          .getElementById('return-date-picker')
          ?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' })
      }

      if (isAboveBreakpoint('lg')) {
        returnRef.current?.expand()
      }
    },
    [returnRef, pickupRef],
  )

  const onReturnChange: DateTimePickerChangeHandler = useCallback(
    (newValues) => {
      handleReturnChange(newValues)

      if (isBelowBreakpoint('lg')) {
        returnRef.current?.collapse()
        document
          .getElementById('location-selector-to-scroll')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
      }

      if (isAboveBreakpoint('lg')) {
        pickupRef.current?.collapse()
        returnRef.current?.collapse()
        document
          .getElementById('date-time-pickers')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
      }
    },
    [returnRef, pickupRef],
  )

  const clearFormDates = () => {
    setFieldValue('startDate', null)
    setFieldValue('pickupAt', null)
    setFieldValue('endDate', null)
    setFieldValue('returnAt', null)
  }

  const resetDateTimePickersToDefaultState = () => {
    const now = new Date()
    pickupRef.current?.reset()
    pickupRef.current?.changeDisplayedMonth(now.getMonth(), now.getFullYear())
    pickupRef.current?.changeVisibleSection(VisibleSectionEnum.DATE)

    returnRef.current?.reset()
    returnRef.current?.changeDisplayedMonth(now.getMonth(), now.getFullYear())
    returnRef.current?.changeVisibleSection(VisibleSectionEnum.DATE)

    if (isBelowBreakpoint('lg')) {
      pickupRef.current?.expand()
      returnRef.current?.collapse()
    } else {
      pickupRef.current?.expand()
      returnRef.current?.expand()
    }
  }

  const resetAmount = () => {
    setFieldValue('amount', 1)
  }

  const onRegionLocationChange = useCallback(
    (selectedRegion: Region | undefined, selectedLocation: Location | undefined) => {
      handleRegionLocationChange(selectedRegion?.id, selectedLocation?.id)

      if (values.selectedLocationId !== selectedLocation?.id) {
        const correctPeriod = checkIfCorrectPeriod(selectedLocation?.id)
        if (!correctPeriod) {
          scrollToTop()
          clearFormDates()
          resetDateTimePickersToDefaultState()
          resetAmount()
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkIfCorrectPeriod, handleRegionLocationChange, values.selectedLocationId, values.selectedRegionId],
  )

  const onAmountChange = (amount: number) => {
    if (amount <= 0) {
      return
    }

    handleAmountChange(amount)
  }

  return (
    <RentalSectionWrapper ref={wrapperRef}>
      <RentalSectionHeader isProlonging={isProlonging} step={step} />

      {step === 1 && (
        <>
          <Text
            fontSize="sm"
            marginTop="5px"
            marginBottom="20px"
            colorPalette="green"
            backgroundColor="green.100"
            padding={2}
            borderRadius="sm"
          >
            {t('noBookingFeeForNext2Hours')}
          </Text>
          <DatePickersWrapper id="date-time-pickers">
            <Box id="pickup-date-picker">
              <DateTimePicker
                title={t('pickup')}
                ref={pickupRef}
                isPickup={true}
                isProlongation={isProlonging}
                onChange={onPickupChange}
                defaultCollapsed={defaultPickupCollapsed}
                shouldDisableDate={shouldDisablePickupDate}
                shouldDisableHour={shouldDisablePickupHour}
                disabled={isProlonging}
                defaultDate={values.startDate}
                defaultTime={values.pickupAt}
              />
            </Box>
            <Box id="return-date-picker">
              <DateTimePicker
                title={t('return')}
                ref={returnRef}
                isPickup={false}
                isProlongation={isProlonging}
                onChange={onReturnChange}
                defaultCollapsed={defaultReturnCollapsed}
                disabled={!values.startDate || !values.pickupAt}
                shouldDisableDate={shouldDisableReturnDate}
                shouldDisableHour={shouldDisableReturnHour}
                defaultVisibleSection={isProlonging ? VisibleSectionEnum.DATE : undefined}
                defaultDate={values.endDate}
                defaultTime={values.returnAt}
              />
            </Box>
          </DatePickersWrapper>

          {product.allowsToRentManyAssets && (
            <Box marginTop="20px" marginBottom="20px">
              <AmountSelector values={values} onAmountChange={onAmountChange} />
            </Box>
          )}

          <Box id="location-selector-to-scroll">
            <LocationSelector
              productLocations={productLocations}
              values={values}
              onRegionLocationChange={onRegionLocationChange}
              checkIfCorrectPeriod={checkIfCorrectPeriod}
              isProlonging={isProlonging}
              checkIfRerent={checkIfRerent}
              setIsRerent={setIsRerent}
              setNextIsDisabled={setNextIsDisabled}
              setRegionHasAvailableLocations={setRegionHasAvailableLocations}
              orderItem={orderItemInfo}
              customerInfo={customerInfo}
              productCalendarList={productCalendarList}
              regions={regions}
              manager={manager}
              sortingInfo={sortingInfoCache}
              setSortingInfo={setSortingInfoCache}
            />
          </Box>
        </>
      )}

      {step === 2 && (
        <>
          <AdditionalOptionsWrapper>
            {!isRerent && (
              <>
                {values.selectedLocationId && submitStatus === 'idle' && !isSubmitting && (
                  <AccessoryItems
                    accessoryItems={product.complementaryItems}
                    fromDate={buildDate(values.startDate, values.pickupAt)}
                    toDate={buildDate(values.endDate, values.returnAt)}
                    selectedLocationId={values.selectedLocationId}
                    setFieldValue={setFieldValue}
                    isEditMode={isEditMode}
                    orderItem={orderItemInfo}
                    setIsRerent={setIsRerent}
                    includeInsurance={values.includeInsurance}
                    includeDeductibles={values.includeDeductibles}
                    customerInfo={customerInfo}
                  />
                )}

                <Box>
                  <IncludeDeductiblesSwitch
                    pricings={product.pricings}
                    fieldName="includeDeductibles"
                    productPrice={productPrice}
                    setFieldValue={setFieldValue}
                    value={values.includeDeductibles}
                    amount={values.amount}
                  />
                </Box>
              </>
            )}
          </AdditionalOptionsWrapper>
        </>
      )}

      <RentalSectionFooter
        isProlonging={isProlonging}
        isEditMode={isEditMode}
        isSubmitting={isSubmitting}
        submitStatus={submitStatus}
        isRentalBeyondMaxTime={isRentalBeyondMaxTime}
        handleSubmit={handleSubmit}
        values={values}
        step={step}
        goToNextStep={goToNextStep}
        goToPrevStep={goToPrevStep}
        touched={touched}
        setTouched={setTouched}
        setValues={setValues}
        isRerent={isRerent}
        product={product}
        productPrice={productPrice}
        nextButtonDisabled={nextIsDisabled}
        regionHasAvailableLocations={regionHasAvailableLocations}
        customerInfo={customerInfo}
        orderItemtInfo={orderItemInfo}
        productLocations={productLocations}
        productPriceLoading={productPriceLoading}
      />

      {product.relatedItems.length !== 0 && (
        <RelatedProducts
          product={product}
          products={product.relatedItems}
          values={values}
          customerInfo={customerInfo}
          sortingInfo={sortingInfoCache}
          isRerent={isRerent}
        />
      )}
    </RentalSectionWrapper>
  )
}

export default RentalSectionClient
